/**
 * Safety numbers (a.k.a. key fingerprints) for MITM protection.
 *
 * Given two users' RSA public-key JWKs we derive a deterministic, symmetric
 * fingerprint both sides can compute independently and compare out-of-band
 * (voice call, in-person, etc.). If the server (or anyone in the path) has
 * swapped a public key, the two users will see DIFFERENT safety numbers —
 * the classic MITM tripwire.
 *
 * Algorithm — same spirit as Signal's "safety numbers":
 *   1. Canonicalise each JWK to a deterministic string (sorted keys).
 *   2. SHA-256 each. The result is a per-user 32-byte "identity hash".
 *   3. Concatenate in sorted order (so Alice↔Bob and Bob↔Alice produce the
 *      same combined input).
 *   4. SHA-256 the combined bytes 5200 times (lightweight KDF) — slows an
 *      attacker's collision search without being noticeable to humans.
 *   5. Take the first 30 decimal digits, group into 5-digit blocks of 6
 *      for readability: `12345 67890 11121 31415 16171 81920`.
 */

const ITERATIONS = 5200;

function canonicalise(jwk) {
  // JSON.stringify with a key-sorting replacer — same shape on both sides.
  if (!jwk || typeof jwk !== 'object') return '';
  const keys = Object.keys(jwk).sort();
  const ordered = {};
  keys.forEach((k) => {
    ordered[k] = jwk[k];
  });
  return JSON.stringify(ordered);
}

async function sha256Bytes(input) {
  const enc = typeof input === 'string' ? new TextEncoder().encode(input) : input;
  const buf = await window.crypto.subtle.digest('SHA-256', enc);
  return new Uint8Array(buf);
}

function bytesCompare(a, b) {
  for (let i = 0; i < Math.min(a.length, b.length); i++) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return a.length - b.length;
}

function bytesConcat(a, b) {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

function bytesToDecimalString(bytes, digits) {
  // Simple big-integer emulation: each byte shifts in 8 bits of the
  // running remainder. We emit `digits` decimal chars by repeatedly
  // taking the running value mod 10^x. Deterministic + portable.
  let big = 0n;
  for (const b of bytes) big = (big << 8n) | BigInt(b);
  const mod = 10n ** BigInt(digits);
  const v = big % mod;
  return v.toString().padStart(digits, '0');
}

/**
 * Compute the 30-digit safety number for a pair of users.
 * `jwkA` / `jwkB` are the RSA public keys (jwk format) exported by each
 * side's `AuthContext.generateKeyPair` call. Order-independent.
 */
export async function computeSafetyNumber(jwkA, jwkB) {
  const hA = await sha256Bytes(canonicalise(jwkA));
  const hB = await sha256Bytes(canonicalise(jwkB));
  // Sort so A↔B matches B↔A.
  const [first, second] = bytesCompare(hA, hB) <= 0 ? [hA, hB] : [hB, hA];
  let combined = bytesConcat(first, second);
  for (let i = 0; i < ITERATIONS; i++) {
    combined = await sha256Bytes(combined);
  }
  const digits = bytesToDecimalString(combined, 30);
  // Group as 6 blocks of 5 digits, space-separated — matches Signal's eye-rhythm.
  return digits.match(/.{1,5}/g).join(' ');
}
